import { Bleed, Card, EmptyState } from '@shopify/polaris';
import { useCurrentApp } from '@/stores/userDataStore';
import VideoCard from '@/components/videos/VideoCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { useQueryStudioVideos } from './hooks/useStudioVideos';
import StudioRoutes from '@/utils/routes';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { StudioVideoStatus } from '../ai-video-creator/stores/studioVideoStore';
import { useCallback, useMemo, memo } from 'react';
import { Product } from '@/types/product';
import EmptyGrid from '@/components/EmptyGrid/EmptyGrid';
interface DraftStudioVideosProps {
  selectedProduct?: Product | null;
}

export const DraftStudioVideos = memo(({ selectedProduct }: DraftStudioVideosProps) => {
  const { pushPath } = useRouterNavigation();
  const { appKey } = useCurrentApp();
  const { studioVideos, isLoading } = useQueryStudioVideos({
    enabled: !!appKey,
    statuses: [StudioVideoStatus.DRAFT],
  });

  const renderStudioVideoItem = useCallback((item) => {
    return <VideoCard key={item.id} video={item} options={{ preload: 'none' }} />;
  }, []);

  const filteredDraftVideos = useMemo(() => {
    if (!selectedProduct) return studioVideos;

    return studioVideos.filter((video) => {
      if (!video.product) return false;

      return video.product.productId === selectedProduct.id || video.product.productId === selectedProduct.productId;
    });
  }, [studioVideos, selectedProduct]);

  if (isLoading || !appKey) {
    return <EmptyGrid staticHeight="calc(100vh - 270px)" />;
  }

  if (!studioVideos?.length) {
    return (
      <Card>
        <EmptyState
          heading="No content available"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          action={{
            content: 'Create a video',
            onAction: () => pushPath(StudioRoutes.createProject()),
          }}
        />
      </Card>
    );
  }

  if (selectedProduct && filteredDraftVideos.length === 0) {
    return (
      <Card>
        <EmptyState
          heading={`No draft videos found for this product`}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          action={{
            content: 'Create a video',
            onAction: () => pushPath(StudioRoutes.createProject()),
          }}
        />
      </Card>
    );
  }

  return (
    <MediaGrid
      items={filteredDraftVideos as { id: string }[]}
      renderItem={renderStudioVideoItem}
      staticHeight="calc(100vh - 270px)"
    />
  );
});

DraftStudioVideos.displayName = 'DraftStudioVideos';
